import type { ResourceCategory } from "@/types/resources"

export const RESOURCE_CATEGORIES: ResourceCategory[] = [
    {
        id: "ai-coding-agents",
        title: "AI Coding Agents",
        description: "Autonomous and semi-autonomous coding agents and editors",
        items: [
            {
                id: "cursor",
                title: "Cursor",
                description:
                    "AI-native code editor built on VS Code with autocomplete and codebase chat.",
                href: "https://cursor.com",
                category: "ai-coding-agents",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "cursor",
                svglRoute: {
                    light: "https://svgl.app/library/cursor_light.svg",
                    dark: "https://svgl.app/library/cursor_dark.svg",
                },
            },
            {
                id: "claude-code",
                title: "Claude Code",
                description:
                    "Anthropic's agentic terminal tool that reads, edits, and runs commands directly in codebases.",
                href: "https://code.claude.com",
                category: "ai-coding-agents",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "claude",
                svglRoute: "https://svgl.app/library/claude-ai-icon.svg",
            },
            {
                id: "antigravity",
                title: "Antigravity 2.0",
                description:
                    "Google's advanced agentic coding environment with background subagents, artifacts, and terminal execution.",
                href: "https://antigravity.google",
                category: "ai-coding-agents",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "antigravity",
            },
            {
                id: "opencode",
                title: "OpenCode",
                description:
                    "Open-source AI coding agent built for the terminal with local and remote LLMs.",
                href: "https://opencode.ai",
                category: "ai-coding-agents",
                iconKey: "opencode",
                svglRoute: {
                    light: "https://svgl.app/library/opencode.svg",
                    dark: "https://svgl.app/library/opencode-dark.svg",
                },
            },
            {
                id: "codex",
                title: "Codex",
                description:
                    "OpenAI's autonomous software engineering environment and agentic framework.",
                href: "https://developers.openai.com/codex",
                category: "ai-coding-agents",
                iconKey: "codex",
                svglRoute: {
                    light: "https://svgl.app/library/codex_light.svg",
                    dark: "https://svgl.app/library/codex_dark.svg",
                },
            },
        ],
    },
    {
        id: "llms-models",
        title: "LLMs & AI Engines",
        description: "Frontier reasoning models and accelerated inference APIs",
        items: [
            {
                id: "claude-opus-fable",
                title: "Claude Opus & Fable",
                description:
                    "Anthropic's flagship frontier reasoning models and Mythos-class architecture for complex agentic workflows.",
                href: "https://claude.ai",
                category: "llms-models",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "claude",
                svglRoute: "https://svgl.app/library/claude-ai-icon.svg",
            },
            {
                id: "gemini",
                title: "Google Gemini",
                description:
                    "Google's frontier multimodal reasoning models with 1M+ token context windows and Deep Research.",
                href: "https://gemini.google.com",
                category: "llms-models",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "gemini",
                svglRoute: "https://svgl.app/library/gemini.svg",
            },
            {
                id: "chatgpt",
                title: "ChatGPT Astra",
                description:
                    "OpenAI's next-generation frontier intelligence model (GPT-6 Astra) with advanced autonomous reasoning.",
                href: "https://chatgpt.com",
                category: "llms-models",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "openai",
                svglRoute: {
                    light: "https://svgl.app/library/openai.svg",
                    dark: "https://svgl.app/library/openai_dark.svg",
                },
            },
            {
                id: "nvidia-nim",
                title: "Nvidia NIM APIs",
                description:
                    "Accelerated inference microservices and cloud endpoints optimized for enterprise AI models on NVIDIA GPUs.",
                href: "https://build.nvidia.com",
                category: "llms-models",
                badge: "essential",
                iconKey: "nvidia",
                svglRoute: "/icons/nvidia.svg",
            },
            {
                id: "openrouter",
                title: "OpenRouter",
                description:
                    "Unified API gateway to route requests across open-source and proprietary models.",
                href: "https://openrouter.ai",
                category: "llms-models",
                badge: "essential",
                iconKey: "openrouter",
                svglRoute: {
                    light: "https://svgl.app/library/openrouter_light.svg",
                    dark: "https://svgl.app/library/openrouter_dark.svg",
                },
            },
            {
                id: "ollama",
                title: "Ollama",
                description:
                    "Run open-source LLMs locally on Apple Silicon with a single command.",
                href: "https://ollama.com",
                category: "llms-models",
                badge: "favorite",
                iconKey: "ollama",
                svglRoute: {
                    light: "https://svgl.app/library/ollama_light.svg",
                    dark: "https://svgl.app/library/ollama_dark.svg",
                },
            },
            {
                id: "zai",
                title: "Z.ai",
                description:
                    "Frontier multimodal reasoning and agentic models powered by the GLM architecture.",
                href: "https://z.ai",
                category: "llms-models",
                iconKey: "zai",
                svglRoute: "/icons/zai.svg",
            },
        ],
    },
    {
        id: "ai-design-tools",
        title: "AI Design Tools",
        description: "Generative UI, component synthesis, and canvas tools",
        items: [
            {
                id: "v0-vercel",
                title: "v0 by Vercel",
                description:
                    "Generative UI system producing production-ready React components with Tailwind CSS.",
                href: "https://v0.dev",
                category: "ai-design-tools",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "vercel",
                svglRoute: {
                    light: "https://svgl.app/library/v0_light.svg",
                    dark: "https://svgl.app/library/v0_dark.svg",
                },
            },
            {
                id: "google-ai-studio",
                title: "Google AI Studio",
                description:
                    "Fast prototyping, multimodal prompt engineering, and app generation with Gemini.",
                href: "https://aistudio.google.com",
                category: "ai-design-tools",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "gemini",
                svglRoute: "https://svgl.app/library/gemini.svg",
            },
            {
                id: "stitch-google",
                title: "Google Stitch",
                description:
                    "Google tool for generating UI screens and code directly from prompts.",
                href: "https://stitch.google.com",
                category: "ai-design-tools",
                iconKey: "stitch",
            },
            {
                id: "paper-design",
                title: "Paper",
                description:
                    "AI-native canvas design tool where every rendered element is real HTML/CSS.",
                href: "https://paper.design",
                category: "ai-design-tools",
                badge: "favorite",
                iconKey: "paper",
                svglRoute: "https://svgl.app/library/paper.svg",
            },
            {
                id: "relume",
                title: "Relume",
                description:
                    "AI tool for rapidly drafting wireframes, sitemaps, and component architecture for Figma.",
                href: "https://relume.io",
                category: "ai-design-tools",
                iconKey: "relume",
                svglRoute: "/icons/relume-colored.svg",
            },
            {
                id: "framer-ai",
                title: "Framer AI",
                description:
                    "Turns natural language prompts into responsive, interactive publishable websites.",
                href: "https://framer.com",
                category: "ai-design-tools",
                iconKey: "framer",
                svglRoute: {
                    light: "https://svgl.app/library/framer.svg",
                    dark: "https://svgl.app/library/framer_dark.svg",
                },
            },
        ],
    },
    {
        id: "dev-tools-terminal",
        title: "Developer Tools & Terminal",
        description: "High-performance editors, terminals, and infrastructure",
        items: [
            {
                id: "ghostty",
                title: "Ghostty",
                description:
                    "Fast, feature-rich, GPU-accelerated terminal emulator with native rendering.",
                href: "https://ghostty.org",
                category: "dev-tools-terminal",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "ghostty",
                svglRoute: "https://svgl.app/library/ghostty.svg",
            },
            {
                id: "zed",
                title: "Zed",
                description:
                    "High-performance multiplayer code editor written from scratch in Rust.",
                href: "https://zed.dev",
                category: "dev-tools-terminal",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "zed",
                svglRoute: {
                    light: "https://svgl.app/library/zed-logo.svg",
                    dark: "https://svgl.app/library/zed-logo_dark.svg",
                },
            },
            {
                id: "biome",
                title: "Biome",
                description:
                    "Lightning-fast toolchain for formatting and linting JavaScript, TypeScript, and CSS.",
                href: "https://biomejs.dev",
                category: "dev-tools-terminal",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "biome",
                svglRoute: "https://svgl.app/library/biomejs.svg",
            },
            {
                id: "cloudflare",
                title: "Cloudflare",
                description:
                    "Edge compute, DNS management, fast static hosting, and worker runtimes.",
                href: "https://cloudflare.com",
                category: "dev-tools-terminal",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "cloudflare",
                svglRoute: "https://svgl.app/library/cloudflare.svg",
            },
            {
                id: "vercel",
                title: "Vercel",
                description:
                    "Frontend cloud platform tailored for Next.js, serverless functions, and edge deployments.",
                href: "https://vercel.com",
                category: "dev-tools-terminal",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "vercel",
                svglRoute: {
                    light: "https://svgl.app/library/vercel.svg",
                    dark: "https://svgl.app/library/vercel_dark.svg",
                },
            },
            {
                id: "supabase",
                title: "Supabase",
                description:
                    "Open-source Firebase alternative providing Postgres, Auth, real-time subscriptions, and storage.",
                href: "https://supabase.com",
                category: "dev-tools-terminal",
                iconKey: "supabase",
                svglRoute: "https://svgl.app/library/supabase.svg",
            },
            {
                id: "docker",
                title: "Docker",
                description:
                    "Containerization system for predictable, isolated local development and deployment.",
                href: "https://docker.com",
                category: "dev-tools-terminal",
                iconKey: "docker",
                svglRoute: "https://svgl.app/library/docker.svg",
            },
            {
                id: "bruno",
                title: "Bruno",
                description:
                    "Fast, open-source, and git-friendly API client that stores collections directly in your filesystem.",
                href: "https://usebruno.com",
                category: "dev-tools-terminal",
                iconKey: "bruno",
            },
            {
                id: "json-forge",
                title: "JSON Forge",
                description:
                    "High-performance JSON editor and visualizer with Monaco editing, interactive graphs, and validation past 10k lines.",
                href: "https://json.manthaa.dev/",
                category: "dev-tools-terminal",
                badge: "favorite",
                iconKey: "json-forge",
                svglRoute: "/icons/json-forge.svg",
            },
        ],
    },
    {
        id: "design-interface",
        title: "Design & Interface",
        description:
            "Vector graphics, collaborative UI layout, and infinite whiteboarding software",
        items: [
            {
                id: "figma",
                title: "Figma",
                description:
                    "The industry-standard collaborative interface design and prototyping tool.",
                href: "https://figma.com",
                category: "design-interface",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "figma",
                svglRoute: "https://svgl.app/library/figma.svg",
            },
            {
                id: "tldraw",
                title: "tldraw",
                description:
                    "Tiny, fast, and infinite collaborative whiteboard for drafting ideas and system flows.",
                href: "https://tldraw.com",
                category: "design-interface",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "tldraw",
                svglRoute: {
                    light: "/icons/tldraw.svg",
                    dark: "/icons/tldraw_dark.svg",
                },
            },
            {
                id: "affinity",
                title: "Affinity Designer",
                description:
                    "High-precision vector graphic design software without perpetual subscription fees.",
                href: "https://affinity.serif.com",
                category: "design-interface",
                iconKey: "affinity",
            },
            {
                id: "canva",
                title: "Canva",
                description:
                    "Rapid asset generation, slide decks, and social media visual creation.",
                href: "https://canva.com",
                category: "design-interface",
                iconKey: "canva",
                svglRoute: "https://svgl.app/library/canva.svg",
            },
        ],
    },
    {
        id: "design-inspiration",
        title: "Design Inspiration & Showcase",
        description:
            "Curated UI/UX patterns, real-world app screens, interaction design, and visual archives",
        items: [
            {
                id: "mobbin",
                title: "Mobbin",
                description:
                    "The world's largest mobile and web design library, showcasing real-world UI patterns, screens, and user flows.",
                href: "https://mobbin.com",
                category: "design-inspiration",
                badge: "favorite",
                iconKey: "mobbin",
                svglRoute: "/icons/mobbin.png",
            },
            {
                id: "dribbble",
                title: "Dribbble",
                description:
                    "Global design community and showcase for UI/UX, product design, and creative inspiration.",
                href: "https://dribbble.com",
                category: "design-inspiration",
                badge: "favorite",
                iconKey: "dribbble",
                svglRoute: "/icons/dribbble.svg",
            },
            {
                id: "pinterest",
                title: "Pinterest",
                description:
                    "Visual discovery engine and moodboarding canvas for UI layouts, architecture, and design aesthetics.",
                href: "https://pinterest.com",
                category: "design-inspiration",
                badge: "favorite",
                iconKey: "pinterest",
                svglRoute: "/icons/pinterest.svg",
            },
            {
                id: "awwwards",
                title: "Awwwards",
                description:
                    "Global platform recognizing and promoting the talent of the best web designers, developers, and agencies.",
                href: "https://www.awwwards.com",
                category: "design-inspiration",
                badge: "favorite",
                iconKey: "awwwards",
            },
            {
                id: "siteinspire",
                title: "Siteinspire",
                description:
                    "A showcase of the finest digital and interactive web design, curated with precision.",
                href: "https://siteinspire.com",
                category: "design-inspiration",
                iconKey: "siteinspire",
                svglRoute: "/icons/siteinspire.svg",
            },
            {
                id: "inspora",
                title: "Inspora",
                description:
                    "Curated archive of visual design, creative UI interactions, motion engineering, and digital craft.",
                href: "https://www.inspora.design",
                category: "design-inspiration",
                badge: "favorite",
                iconKey: "inspora",
            },
            {
                id: "apple-design-awards",
                title: "Apple Design Awards",
                description:
                    "Annual showcase of apps setting the standard for interaction design and craftsmanship.",
                href: "https://developer.apple.com/design/awards",
                category: "design-inspiration",
                badge: "favorite",
                iconKey: "apple-design-awards",
                svglRoute: "/icons/apple-design-awards.svg",
            },
            {
                id: "logosystem",
                title: "Logosystem",
                description:
                    "Curated archive of timeless modernist logos, trademarks, and brand identity systems.",
                href: "https://logosystem.co",
                category: "design-inspiration",
                iconKey: "logosystem",
                svglRoute: {
                    light: "/icons/logosystem.png",
                    dark: "/icons/logosystem_dark.png",
                },
            },
        ],
    },
    {
        id: "ui-components-libraries",
        title: "UI Components & Libraries",
        description:
            "Accessible primitives, copy-paste component systems, and animated React registries",
        items: [
            {
                id: "shadcn-ui",
                title: "shadcn/ui",
                description:
                    "The modern standard: beautifully designed, accessible component primitives you copy and paste into your apps.",
                href: "https://ui.shadcn.com",
                category: "ui-components-libraries",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "shadcn",
                svglRoute: {
                    light: "https://svgl.app/library/shadcn-ui.svg",
                    dark: "https://svgl.app/library/shadcn-ui_dark.svg",
                },
            },
            {
                id: "motion-dev",
                title: "Motion",
                description:
                    "A complete, high-performance animation library for the modern web across React, Vue, and vanilla JS.",
                href: "https://motion.dev",
                category: "ui-components-libraries",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "motion",
            },
            {
                id: "react-bits",
                title: "React Bits",
                description:
                    "An animated, interactive, and fully customizable collection of 60+ React components for modern landing pages.",
                href: "https://reactbits.dev",
                category: "ui-components-libraries",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "reactbits",
            },
            {
                id: "21st-dev",
                title: "21st.dev",
                description:
                    "The npm for Design Engineers: crafted React and Tailwind CSS components ready for you and your AI agent.",
                href: "https://21st.dev",
                category: "ui-components-libraries",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "21st",
            },
            {
                id: "magic-ui",
                title: "Magic UI",
                description:
                    "50+ open-source animated components built with React, TypeScript, Tailwind CSS, and Framer Motion.",
                href: "https://magicui.design",
                category: "ui-components-libraries",
                badge: "favorite",
                iconKey: "magicui",
            },
            {
                id: "aceternity-ui",
                title: "Aceternity UI",
                description:
                    "Trendsetting copy-paste UI components and interactive hero sections built with Tailwind and Motion.",
                href: "https://ui.aceternity.com",
                category: "ui-components-libraries",
                badge: "favorite",
                iconKey: "aceternity",
            },
            {
                id: "motion-primitives",
                title: "Motion Primitives",
                description:
                    "Fluid, gesture-driven motion and micro-interaction components built with Motion and Tailwind CSS.",
                href: "https://motion-primitives.com",
                category: "ui-components-libraries",
                badge: "essential",
                iconKey: "motion-primitives",
            },
            {
                id: "elevenlabs-ui",
                title: "ElevenLabs UI",
                description:
                    "Open-source React components and primitives built on shadcn/ui for conversational AI, voice agents, and audio workflows.",
                href: "https://ui.elevenlabs.io",
                category: "ui-components-libraries",
                badge: "favorite",
                iconKey: "elevenlabs",
            },
        ],
    },
    {
        id: "productivity-utilities",
        title: "Apps & Utilities",
        description:
            "Web browsers, workspace tools, system utilities, and application launchers",
        items: [
            {
                id: "raycast",
                title: "Raycast",
                description:
                    "Supercharged, keyboard-first extensible launcher and command palette.",
                href: "https://raycast.com",
                category: "productivity-utilities",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "raycast",
                svglRoute: "https://svgl.app/library/raycast.svg",
            },
            {
                id: "arc-browser",
                title: "Arc",
                description:
                    "Design-forward, customizable web browser with vertical tabs, spaces, and split views.",
                href: "https://arc.net",
                category: "productivity-utilities",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "arc",
            },
            {
                id: "dia-browser",
                title: "Dia",
                description:
                    "AI-native web browser by The Browser Company that synthesizes tabs and workspaces seamlessly.",
                href: "https://diabrowser.com",
                category: "productivity-utilities",
                badge: "favorite",
                iconKey: "dia",
            },
            {
                id: "notion",
                title: "Notion",
                description:
                    "Flexible workspace for notes, documentation, project wikis, and design specs.",
                href: "https://notion.so",
                category: "productivity-utilities",
                iconKey: "notion",
                svglRoute: "https://svgl.app/library/notion.svg",
            },
            {
                id: "handy",
                title: "Handy",
                description:
                    "Private, open-source local speech-to-text dictation that writes directly into any active text field.",
                href: "https://handy.computer",
                category: "productivity-utilities",
                badge: "favorite",
                iconKey: "handy",
            },
            {
                id: "mole",
                title: "Mole",
                description:
                    "Deep clean, analyze, uninstall, and optimize system performance with a single command or native GUI.",
                href: "https://github.com/tw93/Mole",
                category: "productivity-utilities",
                badge: "essential",
                iconKey: "mole",
            },
            {
                id: "shottr",
                title: "Shottr",
                description:
                    "Lightweight, pixel-perfect screenshot utility with scrolling capture, OCR, and annotations.",
                href: "https://shottr.cc",
                category: "productivity-utilities",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "shottr",
            },
            {
                id: "cap",
                title: "Cap",
                description:
                    "Open-source, high-FPS screen recording alternative to Loom for sharing seamless walkthroughs.",
                href: "https://cap.so",
                category: "productivity-utilities",
                badge: "favorite",
                iconKey: "cap",
                svglRoute: "/icons/cap.svg",
            },
            {
                id: "stats",
                title: "Stats",
                description:
                    "Open-source menu bar utility for real-time CPU, RAM, disk, network, and battery telemetry.",
                href: "https://github.com/exelban/stats",
                category: "productivity-utilities",
                badge: "essential",
                iconKey: "stats",
            },
            {
                id: "maccy",
                title: "Maccy",
                description:
                    "Lightweight, native clipboard manager that keeps your copy history instantly searchable.",
                href: "https://maccy.app",
                category: "productivity-utilities",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "maccy",
            },
            {
                id: "mos",
                title: "Mos",
                description:
                    "Smooth scrolling and independent scroll direction controller for external mice.",
                href: "https://mos.caldis.me",
                category: "productivity-utilities",
                badge: "essential",
                iconKey: "mos",
            },
        ],
    },
    {
        id: "useful-websites",
        title: "Useful Websites & Fonts",
        description:
            "Color palettes, SVG logos, typography directories, and web utilities",
        items: [
            {
                id: "svgl",
                title: "SVGL",
                description:
                    "Searchable, curated library of brand SVG logos for modern web applications and dev tools.",
                href: "https://svgl.app",
                category: "useful-websites",
                badge: "essential",
                iconKey: "svgl",
                svglRoute: "/icons/svgl.svg",
            },
            {
                id: "logoipsum",
                title: "Logoipsum",
                description:
                    "Free, vector SVG placeholder logos across diverse aesthetics for mockups, prototypes, and UI design.",
                href: "https://logoipsum.com",
                category: "useful-websites",
                badge: "favorite",
                iconKey: "logoipsum",
                svglRoute: "/icons/logoipsum.svg",
            },
            {
                id: "fontshare",
                title: "Fontshare",
                description:
                    "Free typography service from the Indian Type Foundry offering high-caliber, professional typefaces.",
                href: "https://fontshare.com",
                category: "useful-websites",
                badge: "favorite",
                iconKey: "fontshare",
                svglRoute: {
                    light: "/icons/fontshare.svg",
                    dark: "/icons/fontshare_dark.svg",
                },
            },
            {
                id: "coolors",
                title: "Coolors",
                description:
                    "Super fast color palettes generator for designers and developers to create, test, and export palettes.",
                href: "https://coolors.co",
                category: "useful-websites",
                badge: "favorite",
                iconKey: "coolors",
            },
            {
                id: "uncut-wtf",
                title: "uncut.wtf",
                description:
                    "Contemporary directory of open-source and free typefaces with strong character.",
                href: "https://uncut.wtf",
                category: "useful-websites",
                badge: "favorite",
                iconKey: "uncut",
                svglRoute: "/icons/uncut.svg",
            },
            {
                id: "cobalt",
                title: "Cobalt",
                description:
                    "Save video, audio, and media from across the web without ads, tracking, or paywalls.",
                href: "https://cobalt.tools",
                category: "useful-websites",
                badge: "favorite",
                iconKey: "cobalt",
            },
            {
                id: "soro",
                title: "Soro",
                description:
                    "Minimalist ambient sound mixer for focus and sleep. Layer nature, binaural beats, and noise loops.",
                href: "https://soro.manthaa.dev/",
                category: "useful-websites",
                badge: "favorite",
                iconKey: "soro",
            },
        ],
    },
    {
        id: "browser-extensions",
        title: "Browser Extensions",
        description:
            "Design inspection, typography analysis, translation, privacy, and development tools",
        items: [
            {
                id: "fonts-ninja",
                title: "Fonts Ninja",
                description:
                    "Inspect, identify, and bookmark typography, font families, and styles on any live webpage.",
                href: "https://www.fonts.ninja",
                category: "browser-extensions",
                badge: "favorite",
                iconKey: "fonts-ninja",
            },
            {
                id: "grid-lines-ruler",
                title: "Grid Lines Ruler",
                description:
                    "Interactive visual ruler and customizable grid lines to measure spacing, alignment, and margins.",
                href: "https://chromewebstore.google.com/detail/grid-lines-ruler/efbjomaifkkegoiioihfcallbkfgibbo",
                category: "browser-extensions",
                badge: "essential",
                iconKey: "grid-lines-ruler",
            },
            {
                id: "proton-vpn",
                title: "Proton VPN",
                description:
                    "Swiss encrypted proxy and fast VPN extension for anonymous, ad-free, and secure browsing.",
                href: "https://protonvpn.com",
                category: "browser-extensions",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "proton-vpn",
            },
            {
                id: "wappalyzer",
                title: "Wappalyzer",
                description:
                    "Technology profiler that uncovers frameworks, libraries, CMS, analytics, and server infrastructure.",
                href: "https://www.wappalyzer.com",
                category: "browser-extensions",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "wappalyzer",
            },
            {
                id: "icloud-passwords",
                title: "iCloud Passwords",
                description:
                    "Seamlessly autofill and sync Apple Keychain passwords, passkeys, and 2FA verification codes.",
                href: "https://chromewebstore.google.com/detail/icloud-passwords/pejdijmoenmkgeppbflobdenhhabjlaj",
                category: "browser-extensions",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "icloud-passwords",
            },
            {
                id: "ublock-origin-lite",
                title: "uBlock Origin Lite",
                description:
                    "Lightweight, permission-minimized content blocker using declarativeNetRequest for fast, efficient ad-blocking.",
                href: "https://github.com/uBlockOrigin/uBOL-home",
                category: "browser-extensions",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "ublock-origin",
            },
        ],
    },
    {
        id: "agent-skills",
        title: "Agent Skills & Workflows",
        description:
            "Curated instructional skills, design rules, and motion engineering for AI coding agents",
        items: [
            {
                id: "emil-skills",
                title: "Emil Kowalski Skills",
                description:
                    "Design and animation engineering skills for AI agents distilled from Vercel & Linear to eliminate interface slop.",
                href: "https://github.com/emilkowalski/skills",
                category: "agent-skills",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "emil-skills",
            },
            {
                id: "apple-design-skill",
                title: "Apple Design Principles",
                description:
                    "Distilled Apple WWDC design language and physics-based fluid interactions translated for the web.",
                href: "https://github.com/emilkowalski/skills/tree/main/skills/apple-design",
                category: "agent-skills",
                badge: "favorite",
                iconKey: "apple",
            },
            {
                id: "skills-sh",
                title: "skills.sh",
                description:
                    "Open directory and CLI registry to discover, install, and version capabilities for AI coding agents.",
                href: "https://skills.sh/emilkowalski/skills",
                category: "agent-skills",
                badge: "daily",
                badgeText: "Daily Driver",
                iconKey: "skills-sh",
            },
        ],
    },
]
